import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";

// Guards against a hung request to Supabase (dropped connection, etc.)
// leaving a "Closing…" / "Saving…" button stuck forever with no feedback.
// 30s gives real-but-slow requests (cold DB connection, weak network) room
// to finish normally instead of being reported as failed while still in
// flight.
const WRITE_TIMEOUT_MS = 30000;
const READ_TIMEOUT_MS = 30000;

// How often to quietly re-sync the list as a backstop for Realtime. Raised
// from the original 20s: each tick is a full table read, and on the admin
// page it used to be two of them. Every Supabase request first calls
// auth.getSession(), which takes a global lock keyed on the auth storage
// key — so a steady drumbeat of background reads is not free, it's
// contention that an admin's Save has to queue behind. 45s is still well
// inside "feels live" territory for a roadmap board.
const POLL_INTERVAL_MS = 45000;

// Both `visibilitychange` and `focus` fire when a tab is brought back to the
// front, and the original code wired a refetch to each of them — so
// returning to the tab kicked off two simultaneous full reloads. Worse, that
// is the exact moment supabase-js itself runs _recoverAndRefresh() and takes
// the auth lock, so those duplicate reads pile up behind a token refresh.
// This floor collapses the duplicates into one.
const BACKGROUND_REFRESH_MIN_INTERVAL_MS = 5000;

// Racing a promise against a timer does NOT cancel the underlying request —
// it keeps running on the network/server after we've already told the user
// it "timed out". If it then succeeds, the write did go through even though
// the UI reported failure, which is confusing and can look like data loss.
// `onLateSettle` lets a caller apply the result if it eventually does land,
// so state stays correct even when the timeout fired first.
const withTimeout = (promise, ms, label, onLateSettle) => {
  // Which of the two (real request vs. timer) settles the race first.
  let winner = null;
  let timer;

  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      winner = winner || "timeout";
      if (winner === "timeout") {
        reject(new Error(`${label} timed out. Please check your connection and try again.`));
      }
    }, ms);
  });

  // Track the real request separately so we can react to it even after
  // the timeout has already won the race and rejected for the caller.
  promise.then(
    (result) => {
      clearTimeout(timer);
      const alreadyTimedOut = winner === "timeout";
      winner = winner || "request";
      if (alreadyTimedOut && onLateSettle) onLateSettle(null, result);
    },
    (err) => {
      clearTimeout(timer);
      const alreadyTimedOut = winner === "timeout";
      winner = winner || "request";
      if (alreadyTimedOut && onLateSettle) onLateSettle(err, null);
    }
  );

  return Promise.race([promise, timeoutPromise]);
};

const CONFIG_ERROR =
  "Database is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.";

// Merges rows from feature_requester_contact (discord_username / email) onto
// their matching feature objects. Only ever populated when isAdmin is true —
// for everyone else this table is unreadable anyway (see
// supabase/migrations/004_requester_contact_privacy.sql), so there's no
// point even asking.
const mergeContacts = (features, contacts) => {
  if (!contacts || contacts.length === 0) return features;
  const byId = new Map(contacts.map((c) => [c.feature_id, c]));
  return features.map((f) => {
    const c = byId.get(f.id);
    return c ? { ...f, discord_username: c.discord_username, email: c.email } : f;
  });
};

// Unique per mount, so a remount never tries to join a Realtime topic that a
// previous instance is still in the middle of leaving. Same-topic churn is a
// known way to get a channel stuck in "joining" until its 10s join timeout.
let channelCounter = 0;
const nextChannelId = () => `${Date.now().toString(36)}-${(channelCounter += 1)}`;

// isAdmin gates whether this hook ever touches feature_requester_contact
// (discord_username / email). Regular/public callers (App.jsx) leave this
// false, so that table is never queried or subscribed to from their
// session — the browser simply never receives that data, rather than
// receiving-and-hiding it. Admin callers (AdminPage.jsx) pass true.
export const useFeatures = (isAdmin = false) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Requester contact rows, kept outside React state so that a plain
  // features refetch can re-merge them without having to re-query the
  // contact table every single time.
  const contactsRef = useRef([]);

  // Monotonic counter identifying the most recent read we care about.
  //
  // THIS IS THE FIX FOR "sometimes it saves instantly, sometimes it waits a
  // while and then gets done." A background poll issued up to 30s ago
  // resolves with a snapshot taken BEFORE the admin's save committed, and
  // then calls setFeatures with the whole array — silently overwriting the
  // freshly-saved row with its old values. The save really did succeed; the
  // UI just got rolled back by a stale read. The next poll (up to 45s later)
  // then shows the new value, which is exactly the "waits for some time and
  // then gets done" behaviour. Every write bumps this counter, so any read
  // that was already in flight is recognised as older than local state and
  // discarded instead of applied.
  const loadSeqRef = useRef(0);

  // How many writes are currently in flight. Background reads are skipped
  // entirely during that window, both to avoid the race above and to keep
  // the auth lock / connection free for the write itself.
  const writesInFlightRef = useRef(0);

  const lastBackgroundLoadRef = useRef(0);

  // Latest isAdmin, readable from callbacks that were created once on mount.
  const isAdminRef = useRef(isAdmin);
  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);

  // Wraps every mutating call so reads that overlap it can be invalidated.
  const trackWrite = useCallback(async (fn) => {
    writesInFlightRef.current += 1;
    try {
      return await fn();
    } finally {
      // Order matters and there is no await between these two lines, so this
      // is effectively atomic: invalidate first, then release. Any read that
      // started before this write finished is now considered stale.
      loadSeqRef.current += 1;
      writesInFlightRef.current -= 1;
    }
  }, []);

  const loadContacts = useCallback(async () => {
    if (!isAdminRef.current) return contactsRef.current;
    const { data, error: contactError } = await supabase
      .from("feature_requester_contact")
      .select("*");
    if (contactError) {
      // Non-fatal: the roadmap itself still loads fine without requester
      // contact info, so just log it and keep whatever we already had.
      console.error("Failed to load requester contact info:", contactError.message);
      return contactsRef.current;
    }
    contactsRef.current = data || [];
    return contactsRef.current;
  }, []);

  const loadFeatures = useCallback(
    async ({ showLoadingSpinner = false } = {}) => {
      if (!isSupabaseConfigured) return;

      // A write in flight means anything we read right now may already be
      // out of date by the time it lands. Don't even start.
      if (!showLoadingSpinner && writesInFlightRef.current > 0) return;

      const seq = (loadSeqRef.current += 1);
      if (showLoadingSpinner) setLoading(true);

      try {
        const [featureResult] = await Promise.all([
          withTimeout(
            supabase.from("features").select("*").order("updated_at", { ascending: false }),
            READ_TIMEOUT_MS,
            "Loading features"
          ),
          // Wrapped so a slow/failed contact query can never keep this
          // Promise.all pending or reject the whole load.
          loadContacts().catch((err) => {
            console.error("Failed to load requester contact info:", err);
          }),
        ]);

        // Superseded: either a newer read started, or a write completed
        // while this one was in flight. Applying it now would clobber
        // newer data with older data.
        if (seq !== loadSeqRef.current) return;

        const { data, error: queryError } = featureResult;

        // Supabase is the only source of roadmap data. A successful query
        // with zero rows is a normal, valid state (handled by the "No
        // features found." empty state in FeatureList), not an error.
        if (queryError) {
          console.error("Supabase error:", queryError.message);
          // Only a foreground load (the initial mount) shows the
          // connection-error banner and clears the list. A background poll
          // or focus/visibility refetch runs silently and on failure just
          // logs it, leaving whatever is already on screen alone.
          if (showLoadingSpinner) {
            setError("Unable to load features. Please try again.");
            setFeatures([]);
          }
          return;
        }

        setError(null);
        setFeatures(mergeContacts(data || [], contactsRef.current));
      } catch (err) {
        // Covers a timed-out/hung request or any unexpected thrown error,
        // so the loading spinner never spins forever.
        console.error("Failed to load features:", err.message);
        if (seq !== loadSeqRef.current) return;
        if (showLoadingSpinner) {
          setError("Unable to load features. Please try again.");
          setFeatures([]);
        }
      } finally {
        if (showLoadingSpinner) setLoading(false);
      }
    },
    [loadContacts]
  );

  const requestBackgroundRefresh = useCallback(() => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastBackgroundLoadRef.current < BACKGROUND_REFRESH_MIN_INTERVAL_MS) return;
    lastBackgroundLoadRef.current = now;
    loadFeatures({ showLoadingSpinner: false });
  }, [loadFeatures]);

  // ---------------------------------------------------------------------
  // Initial load + Realtime + polling. Deps are [] on purpose.
  //
  // This effect used to depend on `isAdmin`, which on the admin page starts
  // false and flips to true the moment the session is restored. That tore
  // the entire effect down and rebuilt it: two full feature loads back to
  // back, setLoading(true) a second time (so "Loading features..." was shown
  // for the duration of BOTH), and a Realtime channel removed and
  // immediately re-created under the same topic name. That double round trip
  // is a large part of the long wait after a refresh. Admin-only data is now
  // handled by its own effect below, so the base lifecycle runs exactly once.
  // ---------------------------------------------------------------------
  useEffect(() => {
    // No fallback data source of any kind: if Supabase isn't configured,
    // fail immediately and visibly instead of attempting a request against
    // a placeholder/dead host (which would just hang) or showing bundled
    // sample data.
    if (!isSupabaseConfigured) {
      setError(CONFIG_ERROR);
      setFeatures([]);
      setLoading(false);
      return;
    }

    loadFeatures({ showLoadingSpinner: true });

    const channel = supabase
      .channel(`features-realtime-${nextChannelId()}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "features" },
        (payload) => setFeatures((prev) =>
          prev.some((f) => f.id === payload.new.id) ? prev : [...prev, payload.new]
        )
      )
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "features" },
        (payload) => setFeatures((prev) => prev.map((f) => (f.id === payload.new.id ? { ...payload.new, discord_username: f.discord_username, email: f.email } : f)))
      )
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "features" },
        (payload) => setFeatures((prev) => prev.filter((f) => f.id !== payload.old.id))
      );

    channel.subscribe();

    // Realtime (postgres_changes) only delivers events if the `features`
    // table has actually been added to Supabase's `supabase_realtime`
    // publication — a one-time DB-level setting (Dashboard → Database →
    // Replication, or `alter publication supabase_realtime add table
    // public.features;`). If that was never done, `channel.subscribe()`
    // still connects "successfully" but silently never receives a single
    // event. So back it up with a quiet periodic refetch and a refetch when
    // the tab becomes visible again. Neither shows the loading spinner.
    const pollId = setInterval(requestBackgroundRefresh, POLL_INTERVAL_MS);

    document.addEventListener("visibilitychange", requestBackgroundRefresh);
    window.addEventListener("focus", requestBackgroundRefresh);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
      document.removeEventListener("visibilitychange", requestBackgroundRefresh);
      window.removeEventListener("focus", requestBackgroundRefresh);
    };
  }, []);

  // ---------------------------------------------------------------------
  // Admin-only: requester contact info. Runs when (and only when) admin
  // status is confirmed, and merges into the existing list in place —
  // no second features query, no spinner, no channel churn.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!isSupabaseConfigured || !isAdmin) return;

    let cancelled = false;

    const channel = supabase
      .channel(`contacts-realtime-${nextChannelId()}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "feature_requester_contact" },
        (payload) => {
          const row = payload.new;
          if (!row) return;
          contactsRef.current = [
            ...contactsRef.current.filter((c) => c.feature_id !== row.feature_id),
            row,
          ];
          setFeatures((prev) =>
            prev.map((f) =>
              f.id === row.feature_id
                ? { ...f, discord_username: row.discord_username, email: row.email }
                : f
            )
          );
        }
      );

    channel.subscribe();

    loadContacts()
      .then(() => {
        if (cancelled) return;
        setFeatures((prev) => mergeContacts(prev, contactsRef.current));
      })
      .catch((err) => console.error("Failed to load requester contact info:", err));

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [isAdmin, loadContacts]);

  const fetchFeatures = useCallback(
    () => loadFeatures({ showLoadingSpinner: false }),
    [loadFeatures]
  );

  // Posted straight from the browser to the Discord webhook — no serverless
  // function in front of it. This project is hosted on AWS Amplify static
  // hosting (no Vercel/Netlify-style function runtime available, and no
  // Amplify Gen 2 backend in this repo either), and per product decision the
  // webhook URL is hardcoded here rather than read from an env var, so it
  // doesn't depend on Amplify's build-time environment variables being
  // configured correctly. That means anyone who opens devtools (or reads
  // this file) can read the URL and post into the channel directly; if that
  // ever becomes a problem, front this webhook with a server-side proxy
  // instead and rotate this URL in Discord's webhook settings.
  const DISCORD_WEBHOOK_URL =
    "https://discord.com/api/webhooks/1394916870792155238/pccu-foEzYUwig6EfQn6gb4zJo8NfslYa5EQ_9byZjzxEbL6gjTZyr8rGtMDGVRFi5_M";

  const PRIORITY_EMOJIS = { Urgent: "🚨", High: "⚠️", Medium: "📋", Low: "📝" };

  const clip = (value, max) => {
    const str = (value ?? "").toString();
    return str.length > max ? `${str.slice(0, max - 1)}…` : str || "—";
  };

  const sendDiscordNotification = async (featureData) => {
    const webhookUrl = DISCORD_WEBHOOK_URL;

    const priorityLabel =
      (featureData.priority || "medium").toString().charAt(0).toUpperCase() +
      (featureData.priority || "medium").toString().slice(1).toLowerCase();

    // Everything filled in on the request form is sent to Discord EXCEPT the
    // requester's Discord username and email — those two fields are private
    // contact info (see supabase/migrations/004_requester_contact_privacy.sql)
    // and must never appear in the notification embed.
    const embed = {
      title: `${PRIORITY_EMOJIS[priorityLabel] || "🚀"} New Feature Request — ${priorityLabel} Priority`,
      color: 0x7c3aed,
      fields: [
        { name: "📝 Feature", value: clip(featureData.feature, 256), inline: false },
        { name: "📋 Description", value: clip(featureData.description, 1000), inline: false },
        { name: "🎯 Use case", value: clip(featureData.use_case, 500), inline: false },
        { name: "🏷️ Category", value: clip(featureData.category, 100), inline: true },
        { name: "⚡ Priority", value: priorityLabel, inline: true },
        { name: "📌 Status", value: clip(featureData.status, 100), inline: true },
        ...(featureData.justification
          ? [{ name: "❗ Justification", value: clip(featureData.justification, 1000), inline: false }]
          : []),
        { name: "📅 Submitted", value: new Date().toLocaleString(), inline: true },
      ],
      footer: { text: "Greta Roadmap Tracker" },
    };

    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ embeds: [embed] }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error("[discord-notify] Discord webhook responded with an error:", res.status, text);
        return { ok: false, status: res.status, body: text };
      }

      return { ok: true };
    } catch (err) {
      console.error("[discord-notify] Failed to reach Discord webhook:", err);
      // Never let a notification failure block or surface as an error on the
      // feature request itself — the DB write already succeeded regardless.
      return { ok: false, reason: "network-error", error: err?.message };
    }
  };

  // Used by both the public "Request a Feature" form and the admin
  // "Add Feature to Roadmap" form. Public submissions never include a
  // `status` (the public insert RLS policy only allows "Requested" anyway),
  // so they fall back to "Requested" as before. Admin submissions DO include
  // whichever status the admin picked in the form (see SuggestFeatureModal's
  // `status` select, only rendered when isAdmin), and that value is now
  // respected here instead of being silently overwritten — previously every
  // admin-added feature landed in "Requested" regardless of the selected
  // section, requiring a manual edit afterward to move it.
  const createFeature = async (featureData) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);
    const row = {
      feature: featureData.feature || featureData.title || "",
      description: featureData.description || "",
      status: featureData.status || "Requested",
      priority: (featureData.priority || "medium").toLowerCase(),
      category: featureData.category || "",
      use_case: featureData.use_case || null,
      justification: featureData.justification || null,
      upvotes: 0,
      // Only ever an explicitly-supplied public display name. Do NOT fall
      // back to discord_username here: `requested_by` IS a plain column on
      // `features`, so anything written to it goes out through the public
      // select("*") and the Realtime stream.
      requested_by: featureData.requestedBy || null,
    };

    const { data, error: insertError } = await trackWrite(() =>
      supabase.from("features").insert([row]).select().single()
    );
    if (insertError) throw insertError;

    const discord_username = featureData.discord_username || null;
    const email = featureData.email || null;
    let resultData = data;
    if (discord_username || email) {
      const { error: contactError } = await trackWrite(() =>
        supabase
          .from("feature_requester_contact")
          .insert([{ feature_id: data.id, discord_username, email }])
      );
      if (contactError) {
        // Don't fail the whole submission over the contact-info write — the
        // roadmap item itself was created successfully. Just log it.
        console.error("Failed to save requester contact info:", contactError.message);
      } else {
        resultData = { ...data, discord_username, email };
      }
    }

    setFeatures((prev) =>
      prev.some((f) => f.id === resultData.id) ? prev : [...prev, resultData]
    );
    // discord_username / email deliberately excluded — see
    // sendDiscordNotification's comment above.
    await sendDiscordNotification(row);
    return resultData;
  };

  // Generic admin update (status, priority, edits, etc.)
  const updateFeature = async (id, updates) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);

    const applyLateResult = (err, result) => {
      // The UI already showed a timeout error for this action. If the write
      // actually succeeded on the server after all, don't leave the local
      // list silently out of sync with the database.
      if (err) {
        console.error("Update failed after timeout was already reported:", err);
        return;
      }
      const { data, error: lateError } = result;
      if (lateError) {
        console.error("Update failed after timeout was already reported:", lateError);
        return;
      }
      console.warn(
        `Update for feature ${id} completed after the timeout had already been shown; applying the result now.`
      );
      loadSeqRef.current += 1;
      setFeatures((prev) =>
        prev.map((f) =>
          f.id === id ? { ...data, discord_username: f.discord_username, email: f.email } : f
        )
      );
    };

    const { data, error: updateError } = await trackWrite(() =>
      withTimeout(
        supabase.from("features").update(updates).eq("id", id).select().single(),
        WRITE_TIMEOUT_MS,
        "Update",
        applyLateResult
      )
    );

    if (updateError) {
      // Log the FULL error object (not just .message) so the browser console
      // shows the real Postgres/PostgREST code, details, and hint.
      console.error("updateFeature failed:", { id, updates, error: updateError });

      // PGRST116 = "no rows returned" from .single() — happens when the
      // UPDATE matched zero rows, which for this table almost always means
      // Row Level Security silently rejected the write.
      if (updateError.code === "PGRST116") {
        throw new Error(
          "The update was rejected by the database. You may no longer be recognized as an admin — try logging out and back in."
        );
      }
      // 23514 = Postgres check-constraint violation.
      if (updateError.code === "23514") {
        throw new Error(
          `The database rejected this update (constraint violation): ${updateError.message}`
        );
      }
      throw new Error(updateError.message || "Failed to save changes. Please try again.");
    }

    // `data` comes back from `features` only, which no longer has
    // discord_username/email — carry over whatever was already merged onto
    // the existing entry (admin sessions only) instead of wiping it out.
    setFeatures((prev) =>
      prev.map((f) =>
        f.id === id ? { ...data, discord_username: f.discord_username, email: f.email } : f
      )
    );
    return data;
  };

  // Admin: close a request with a required closing comment.
  const closeFeature = async (id, closingComment) => {
    if (!closingComment || !closingComment.trim()) {
      throw new Error("A closing comment is required to close a request.");
    }
    return updateFeature(id, {
      status: "Closed",
      closing_comment: closingComment.trim(),
      closed_at: new Date().toISOString(),
    });
  };

  // Admin: reopen a previously closed request back to "Requested".
  const reopenFeature = async (id) => {
    return updateFeature(id, {
      status: "Requested",
      closing_comment: null,
      closed_at: null,
    });
  };

  // Admin: mark a request/roadmap item as Released, with an optional release note.
  const releaseFeature = async (id, releaseNote) => {
    return updateFeature(id, {
      status: "Released",
      release_note: releaseNote ? releaseNote.trim() : null,
      released_at: new Date().toISOString(),
    });
  };

  const deleteFeature = async (id) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);
    const { error: deleteError } = await trackWrite(() =>
      withTimeout(supabase.from("features").delete().eq("id", id), WRITE_TIMEOUT_MS, "Delete")
    );
    if (deleteError) throw deleteError;
    setFeatures((prev) => prev.filter((f) => f.id !== id));
  };

  const upvoteFeature = async (id) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);
    await trackWrite(async () => {
      const { error: rpcError } = await supabase.rpc("increment_upvotes", { feature_id: id });
      if (rpcError) {
        const f = features.find((x) => x.id === id);
        await supabase.from("features").update({ upvotes: (f?.upvotes || 0) + 1 }).eq("id", id);
      }
    });
    // The RPC/update above only changes the database — it doesn't return
    // the new row, and neither Realtime nor the visibility-triggered
    // background refresh fires synchronously, so without this the card's
    // displayed count was stuck at the old value until the next poll tick
    // or a tab refocus. Bump it locally right away so the click feels
    // instant; if Realtime later delivers the authoritative row it will
    // just overwrite this with the same (or newer) value.
    setFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, upvotes: (f.upvotes || 0) + 1 } : f))
    );
  };

  // Admin-only: internal notes live in their own table (feature_internal_notes)
  // with RLS restricted to admins, never the `features` table itself — see
  // supabase/migrations/003_internal_notes.sql for why.
  const getInternalNote = async (featureId) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);
    const { data, error: noteError } = await supabase
      .from("feature_internal_notes")
      .select("notes, updated_at")
      .eq("feature_id", featureId)
      .maybeSingle();
    if (noteError) throw noteError;
    return data || { notes: "", updated_at: null };
  };

  const saveInternalNote = async (featureId, notes) => {
    if (!isSupabaseConfigured) throw new Error(CONFIG_ERROR);

    // getSession() reads the already-decoded session (and its user) from
    // local storage. The previous getUser() call here was a full network
    // round trip to /auth/v1/user on EVERY note save, taken while holding
    // the auth lock, purely to read an id we already have locally — so the
    // upsert couldn't even begin until it came back.
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const { data, error: saveError } = await trackWrite(() =>
      withTimeout(
        supabase
          .from("feature_internal_notes")
          .upsert(
            {
              feature_id: featureId,
              notes: notes || "",
              updated_at: new Date().toISOString(),
              updated_by: session?.user?.id || null,
            },
            { onConflict: "feature_id" }
          )
          .select("notes, updated_at")
          .single(),
        WRITE_TIMEOUT_MS,
        "Saving note"
      )
    );
    if (saveError) throw saveError;
    return data;
  };

  return {
    features,
    loading,
    error,
    fetchFeatures,
    createFeature,
    updateFeature,
    closeFeature,
    reopenFeature,
    releaseFeature,
    deleteFeature,
    upvoteFeature,
    getInternalNote,
    saveInternalNote,
  };
};
